import {Bell, Check, Eye, X} from 'lucide-react';
import {useApiAcceptNotifications, useApiRejectNotifications, useApiSeenNotifications} from "@/api/notification";
import RenderIf from "@/components/render-if";
import {Button} from "antd";
import {convertToJalaliWithFormat} from "@/lib/utils/date";
import NotificationButton from "@/pages/notification/_section/NotificationButton";


export default function NotificationCard({notification, isProcessing, refetch}) {

    const {mutate: Seen, isPending: isPendingSeen} = useApiSeenNotifications();
    const {mutate: Reject, isPending: isPendingReject} = useApiRejectNotifications();
    const {mutate: Accept, isPending: isPendingAccept} = useApiAcceptNotifications();

    const isLoading = isPendingSeen || isPendingReject || isPendingAccept;

    const getActionIcon = (key) => {
        switch (key) {
            case 'seen':
                return <Eye className="w-4 h-4"/>;
            case 'approve':
            case 'accept':
                return <Check className="w-4 h-4"/>;
            case 'reject':
            case 'decline':
                return <X className="w-4 h-4"/>;
            default:
                return null;
        }
    };

    const getActionStyle = (key) => {
        switch (key) {
            case 'approve':
            case 'accept':
                return 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:hover:bg-emerald-900/50 dark:border-emerald-800';
            case 'reject':
            case 'decline':
                return 'bg-red-50 text-red-700 hover:bg-red-100 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:hover:bg-red-900/50 dark:border-red-800';
            case 'seen':
                return 'bg-slate-50 text-slate-700 hover:bg-slate-100 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 dark:border-slate-600';
            default:
                return 'bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:hover:bg-blue-900/50 dark:border-blue-800';
        }
    };

    function onAction(action, key) {
        switch (key) {
            case 'accept':
                Accept({uuid: action}, {
                    onSuccess: (e) => {
                        refetch()
                    }
                })
                break;
            case 'reject':
                Reject({uuid: action}, {
                    onSuccess: (e) => {
                        refetch()
                    }
                })
                break;
            case 'seen':
                Seen({uuid: action}, {
                    onSuccess: (e) => {
                        refetch()
                    }
                })
                break;
        }
    }

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

        if (diffInSeconds < 60) return 'الان';
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} دقیقه پیش`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} ساعت پیش`;
        if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} روز پیش`;

        return date.toLocaleDateString('fa-IR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <div
            className="
                rounded-xl shadow-sm
                border dark:border-gray-700 border-slate-200
                p-6
                hover:shadow-md dark:hover:shadow-black/20
                transition-all duration-300
                hover:border-slate-300 dark:hover:border-gray-600
                my-1
                bg-white dark:bg-gray-800
            ">
            <div className="flex flex-wrap items-start gap-4">
                <div>
                    <div
                        className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                        <Bell className="w-6 h-6 text-white"/>
                    </div>
                </div>

                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap justify-between gap-4 mb-3">
                        <p className="text-base leading-relaxed text-gray-800 dark:text-gray-200" dir="rtl">
                            {notification.message}
                        </p>

                        <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap" dir="rtl">
                            {formatDate(notification.created_at)}
                        </span>
                    </div>

                    <RenderIf condition={notification.actions && notification.actions.length > 0}>
                        <div className="flex flex-wrap gap-2" dir="rtl">
                            <NotificationButton notification={notification}/>
                            {notification.actions.map((action) => {
                                if (action.key === 'seen' && notification.read_at) {
                                    return null;
                                }

                                if ((action.key === 'accept' || action.key === 'reject') &&
                                    (notification.accept_at || notification.reject_at)) {

                                    if (action.key === 'accept' && notification.accept_at) {
                                        return (
                                            <small key={action.key + '-' + notification.id}
                                                   className="text-green-800 dark:text-green-400">
                                                تایید شده در
                                                {convertToJalaliWithFormat(notification.accept_at, 'jYYYY/jMM/jDD HH:MM:ss')}
                                            </small>
                                        )
                                    }

                                    if (action.key === 'reject' && notification.reject_at) {
                                        return (
                                            <small key={action.key + '-' + notification.id}
                                                   className="text-red-800 dark:text-red-400">
                                                رد شده در
                                                {convertToJalaliWithFormat(notification.reject_at, 'jYYYY/jMM/jDD HH:MM:ss')}
                                            </small>
                                        )
                                    }

                                    return null;
                                }

                                return (
                                    <div key={action.key + '-' + notification.id}>
                                        <Button
                                            loading={isLoading}
                                            onClick={() => onAction(notification.id, action.key)}
                                            className={`
                                                inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
                                                border transition-all duration-200
                                                disabled:opacity-50 disabled:cursor-not-allowed
                                                ${getActionStyle(action.key)}
                                            `}
                                        >
                                            {getActionIcon(action.key)}
                                            <span>{action.title}</span>
                                        </Button>
                                    </div>
                                )
                            })}
                        </div>
                    </RenderIf>
                </div>
            </div>
        </div>
    );
}